/**
 * Minefield cubo pricing (forum / spreadsheet model).
 * Project cubo: SUMPRODUCT-style marginal adjustment on block count, then × 0.15 PA/block.
 * Same vectors as the published GSheet: thresholds 0, 1500, 15000, …, 260000 and rates
 * 0, 0.35, then 0.05 for each further threshold. Rows apply only when area > threshold
 * (e.g. 450×450 = 202 500 < 220 000 so the last two rows do not contribute).
 */

export const UNIT_PA_PROJECT = 0.15;
export const UNIT_PA_PLAYER = 0.5;

/** @type {readonly number[]} */
export const PROJECT_SUMPRODUCT_THRESHOLDS = Object.freeze([
  0, 1500, 15000, 30000, 50000, 70000, 90000, 110000, 140000, 180000, 220000, 260000,
]);

/** @type {readonly number[]} */
export const PROJECT_SUMPRODUCT_RATES = Object.freeze([
  0, 0.35, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05,
]);

/**
 * @param {number} area - block count (positive integer in practice)
 * @returns {number} inner term before × UNIT_PA_PROJECT (same as GSheet before ROUND)
 */
export function computeProjectSumProductAdjustment(area) {
  let sum = 0;
  for (let i = 0; i < PROJECT_SUMPRODUCT_THRESHOLDS.length; i++) {
    const t = PROJECT_SUMPRODUCT_THRESHOLDS[i];
    const r = PROJECT_SUMPRODUCT_RATES[i];
    if (area > t) {
      sum += (area - t) * r;
    }
  }
  return sum;
}

/**
 * Effective “billable” block count after marginal adjustment.
 * @param {number} area
 */
export function computeProjectEffectiveBlocks(area) {
  return area - computeProjectSumProductAdjustment(area);
}

/**
 * Google Sheets ROUND(x, 2): half away from zero at centimes.
 * @param {number} x
 */
export function roundPa2(x) {
  const m = 100;
  return Math.sign(x) * Math.round(Math.abs(x) * m + Number.EPSILON) / m;
}

/**
 * @param {number} area
 * @returns {number} PA, 2 decimal places
 */
export function computeProjectCuboPa(area) {
  const raw = computeProjectEffectiveBlocks(area) * UNIT_PA_PROJECT;
  return roundPa2(raw);
}

/**
 * @param {number} area
 * @returns {number} PA, 2 decimal places
 */
export function computePlayerCuboPa(area) {
  return roundPa2(area * UNIT_PA_PLAYER);
}

/**
 * Inclusive Minecraft-style corners: both corner blocks count.
 * @param {number} x1
 * @param {number} z1
 * @param {number} x2
 * @param {number} z2
 * @returns {number}
 */
export function computeAreaFromCorners(x1, z1, x2, z2) {
  const w = Math.abs(x1 - x2) + 1;
  const d = Math.abs(z1 - z2) + 1;
  return w * d;
}
